/**
 * Quadrant corner names (plots plan O, owner decision 6).
 *
 * A recipe names its map's four corners by `deck_labels.py` keys, in the
 * recipe order (top right, top left, bottom right, bottom left), all four or
 * none. The deck receives them resolved in the deck language; a summary card
 * receives the keys and resolves them in the viewer's UI language. A card
 * stored before step O carries four display strings, printed as stored.
 */

import { QuadrantCornerLabels } from '@digital-twins/slide-schemas/generated/plot-types'

/** The recipe order of the four corner names. */
const QUADRANT_CORNER_POSITIONS = ['topRight', 'topLeft', 'bottomRight', 'bottomLeft'] as const

function isFourNames(values: ReadonlyArray<unknown> | null | undefined): values is ReadonlyArray<string> {
  return (
    Array.isArray(values) &&
    values.length === QUADRANT_CORNER_POSITIONS.length &&
    values.every((value) => typeof value === 'string' && value.trim().length > 0)
  )
}

/** Four names in recipe order as the four named corners; null unless all four exist. */
export function cornerLabelsFromTuple(values: ReadonlyArray<unknown> | null | undefined): QuadrantCornerLabels | null {
  if (!isFourNames(values)) {
    return null
  }
  return {
    topRight: values[0],
    topLeft: values[1],
    bottomRight: values[2],
    bottomLeft: values[3],
  }
}

/**
 * Four recipe keys resolved by the surface's own translation; null unless all
 * four resolve to a name (a missing translation names no corner at all).
 */
export function cornerLabelsFromKeys(
  keys: ReadonlyArray<unknown> | null | undefined,
  translate: (key: string) => string | null | undefined,
): QuadrantCornerLabels | null {
  if (!isFourNames(keys)) {
    return null
  }
  const names = keys.map((key) => translate(key) ?? '')
  return cornerLabelsFromTuple(names)
}
